import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Typography,
  CircularProgress
} from '@mui/material';
import { useSearchContext } from '../../contexts/SearchContext';

interface UpdateSearchDialogProps {
  open: boolean;
  onClose: () => void;
  searchName: string;
}

/**
 * Dialog for confirming update of an existing search
 * Uses the enhanced SearchContext for updating searches
 */
const UpdateSearchDialog: React.FC<UpdateSearchDialogProps> = ({ 
  open, 
  onClose, 
  searchName 
}) => {
  // Get updateLoadedSearch function and state from SearchContext
  const { updateLoadedSearch, isLoading, error: contextError } = useSearchContext();
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Use the error from context if available, otherwise use local error
  const error = contextError || localError;
  
  const handleUpdate = async () => {
    setLocalError(null);
    try {
      const success = await updateLoadedSearch();
      if (success) {
        onClose();
      }
      // We don't need to set errors here as the context will handle that
    } catch (err) {
      console.error("Error updating search:", err);
      setLocalError('An unexpected error occurred during update.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Search</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Update <Typography component="span" fontWeight="bold">{searchName}</Typography> with the current settings?
        </DialogContentText>
        <DialogContentText sx={{ mt: 1 }}>
          This will replace the previously saved search with the current location, date and services.
        </DialogContentText>
        {error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button 
          onClick={handleUpdate} 
          variant="contained" 
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateSearchDialog;
